/** A seller's stats tab: sales, visits and interest, reputation, and where their prices and the demand stand. */
import type { SellerStats } from "./market";

/** Periods the tab offers, in days; 0 is since the shop opened. */
export const STATS_PERIODS = [30, 90, 0] as const;
export type StatsPeriod = (typeof STATS_PERIODS)[number];
export const isStatsPeriod = (n: number): n is StatsPeriod => (STATS_PERIODS as readonly number[]).includes(n);

export interface StatsListing {
  id: number;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  priceArs: number;
}

export interface SellerStatsView {
  period: StatsPeriod;
  /** Sales completed in the period (by completion date); revenue is what the cards sold for, without shipping. */
  sales: { count: number; revenueArs: number; avgTicketArs: number | null };
  /** Cancelled in the period (by cancellation date). */
  cancelled: number;
  /** Orders open right now, whatever the period. */
  inProgress: number;
  /** Completed sales per week (30/90 days) or per month (all), Argentina time, empty ones included. */
  bucket: "week" | "month";
  series: { start: string; count: number; revenueArs: number }[];
  /** Visits to listing pages in the period; counted since viewsSince (null: none yet). */
  views: number;
  viewsSince: string | null;
  /** Saves of their current listings and followers of the shop, now. */
  favorites: number;
  followers: number;
  /** Offers received in the period and how many were accepted. */
  offers: { received: number; accepted: number };
  /** Last 90 days, as the public seller page shows it. */
  reputation: SellerStats;
  /** Most visited listings in the period (then most saved). */
  top: (StatsListing & { status: "active" | "paused"; views: number; favorites: number; sold: number })[];
  /** Live ungraded listings with a cheaper live offer for the same card, condition, language and finish. */
  pricing: (StatsListing & { condition: string; language: string; cheaper: number; marketMinArs: number; storeArs: number | null })[];
  /** "Busco" saved by others for products the seller has live; who is never shown. */
  demand: (StatsListing & { wanting: number; belowYou: number; highestBelowArs: number | null })[];
}

/** Where a counted visit came from (listing_view_sources). Anything unknown is "other". */
export const VIEW_SOURCES = ["search", "product", "shop", "featured", "email", "qr", "external", "other"] as const;
export type ViewSource = (typeof VIEW_SOURCES)[number];
export const asViewSource = (v: unknown): ViewSource => ((VIEW_SOURCES as readonly unknown[]).includes(v) ? (v as ViewSource) : "other");

/**
 * The source of a visit to the card's other offers (not the one a link led to): a shop's own way in (its
 * page, email, QR, featured tile) is that shop's, and an outside link to one offer is that offer's; the
 * rest were seen on the card's page. A search or an outside link to the card itself stays as it is.
 */
export function otherOffersSource(src: ViewSource, linked: boolean): ViewSource {
  if (src === "shop" || src === "featured" || src === "email" || src === "qr") return "product";
  if (linked && src === "external") return "product";
  return src;
}

/** VMAX: where visits come from, how fast cards sell, and listings that need a push. */
export interface SellerPerformance {
  period: StatsPeriod;
  /** Visits by source in the period (only since sources are recorded), biggest first. */
  sources: { source: ViewSource; views: number; pct: number }[];
  /** Median days from listing to its first completed sale, over sales in the period. */
  rotation: { medianDays: number | null; samples: number };
  /** Live for 30 days or more and not one visit in the last 30. */
  noViews: StatsListing[];
  /** Saved as favorites but never sold. */
  savedNotSold: (StatsListing & { favorites: number; featurable: boolean })[];
}

/** Insights (EX and up, PLAN_PERKS.insights): the funnel and market price of each listing, and how fast the shop answers and delivers. */
export const FUNNEL_PAGE_SIZE = 50;

export interface FunnelRow extends StatsListing {
  status: "active" | "paused";
  /** In the period: visits, saves, offers received, completed orders with it and units sold. */
  views: number;
  favorites: number;
  offers: number;
  orders: number;
  units: number;
  /** Orders per 100 visits, counted only since visits are recorded; null without visits. */
  conversionPct: number | null;
}

/** A live ungraded listing against other shops' live offers for the same card, condition, language and finish. */
export interface PriceReference extends StatsListing {
  condition: string;
  language: string;
  /** How many other offers; min and median of their prices (null when nobody else sells it). */
  others: number;
  minArs: number | null;
  medianArs: number | null;
}

export interface SellerInsights {
  period: StatsPeriod;
  funnel: { rows: FunnelRow[]; total: number; page: number };
  prices: PriceReference[];
  /** Last 30 days, medians: buyer's first message to the shop's first answer, and paid to shipped. */
  times: { responseMinutes: number | null; responseSamples: number; dispatchHours: number | null; dispatchSamples: number };
}

export interface TodoOrder {
  number: string;
  buyerName: string;
  /** When it started waiting on the shop. */
  since: string;
}

/** What the shop has to do today; each list is the oldest first, at most TODO_ROWS, with its full count. */
export interface SellerTodo {
  /** The buyer says they paid: confirm it arrived. */
  confirmPayment: { count: number; orders: TodoOrder[] };
  /** Unpaid for over a day: nudge the buyer or cancel. */
  unpaid: { count: number; orders: TodoOrder[] };
  /** Paid (or cash confirmed): ship or hand over. */
  deliver: { count: number; orders: TodoOrder[] };
  /** The buyer wrote last in the order's chat. */
  unanswered: { count: number; orders: TodoOrder[] };
  /** Reviews of the shop without its reply. */
  reviews: { count: number; orders: TodoOrder[] };
}
export const TODO_ROWS = 5;
/** The orders CSV covers at most this many days per download. */
export const ORDERS_CSV_MAX_DAYS = 366;

/** Mi tienda › Inicio: this week against the one before, and what needs a look. */
/**
 * "Para hacer hoy": one task per kind, worked out from the shop's orders, offers and listings (never
 * stored). The order-based ones carry their oldest orders (at most TODO_ROWS).
 */
export const SELLER_TASK_KINDS = ["confirm_payment", "unanswered", "offers", "connect_mp", "deliver", "meeting", "unpaid", "reviews", "soldout", "coupon_ending", "verify"] as const;
export type SellerTaskKind = (typeof SELLER_TASK_KINDS)[number];
export type TaskSeverity = "high" | "medium" | "low";
export interface SellerTask {
  kind: SellerTaskKind;
  severity: TaskSeverity;
  count: number;
  orders: TodoOrder[];
  /** Every thing behind it (orders, offers, listings...): dismissing the task keeps these off the list. */
  keys: string[];
}
/** A dismissal names at most this many things, each this long at most. */
export const DISMISS_MAX_KEYS = 500;
export const DISMISS_KEY_CHARS = 64;
/** Inicio shows this many tasks; "Ver todas" the rest. */
export const HOME_TASKS = 5;
/** Over this many sales without the "Verificado" badge, asking for it becomes a task. */
export const VERIFY_AFTER_SALES = 3;

/** Mi tienda's quick search: one of the shop's orders or listings, and where it opens. */
export interface PanelSearchHit {
  kind: "order" | "listing";
  label: string;
  detail: string;
  href: string;
}
export const PANEL_SEARCH_MIN = 2;
export const PANEL_SEARCH_MAX = 10;

/** At this much over the others' median, a listing's price is flagged ("18% arriba del mercado"). */
export const ABOVE_MARKET_PCT = 20;

/** How far over the others' median a price is, in whole %: null when it's alone, the cheapest, or not above. */
export function aboveMedianPct(priceArs: number, others: number, minArs: number | null, medianArs: number | null): number | null {
  if (others === 0 || medianArs === null || minArs === null || priceArs <= minArs) return null;
  const pct = Math.round(((priceArs - medianArs) / medianArs) * 100);
  return pct > 0 ? pct : null;
}

/** Mi tienda › Publicaciones: what each listing's row says about it. */
export interface ListingSignal {
  id: number;
  /** Visits in the last 30 days. */
  views30d: number;
  /** Live for over 30 days and nobody looked (as Inicio counts them). */
  noViews: boolean;
  /** People with this card on the Se busca board (not the shop itself). */
  wanting: number;
  /** % over the market's median, from ABOVE_MARKET_PCT (plans with insights; null otherwise). */
  abovePct: number | null;
}

export interface HomeOrder {
  number: string;
  buyerName: string;
  status: string;
  totalArs: number;
  createdAt: string;
  /** The buyer says they paid (a transfer to confirm). */
  buyerMarkedPaid: boolean;
  unread: boolean;
}
export interface HomeMessage {
  number: string;
  buyerName: string;
  /** The start of the message. */
  body: string;
  createdAt: string;
  unread: boolean;
}

export interface SellerHome {
  /** The last 7 days and the 7 before them (completed sales; visits to the shop's listings; new followers). */
  week: { salesArs: number; orders: number; views: number; prevSalesArs: number; prevOrders: number; prevViews: number; followers: number };
  /** Completed sales per 7 days, the last 8 of them, oldest first. */
  weeks: number[];
  /** Live listings out for over 30 days without a visit in the last 30. */
  noViews30d: number;
  /** Completed sales ever (0: the start checklist shows instead of the week). */
  completedSales: number;
  /** Live listings out of stock (not because of a reservation). */
  shop: { soldOut: number };
  /** "Para hacer hoy", the most urgent first. */
  tasks: SellerTask[];
  /** Kinds with something the shop dismissed still waiting ("Volver a mostrar"). */
  hiddenTasks: number;
  /** The last orders and the buyers' last messages, newest first. */
  recentOrders: HomeOrder[];
  recentMessages: HomeMessage[];
}

/** A paid perk the shop's plan doesn't have, shown with the shop's own number (see server/lib/market/showcase.ts). */
export type ShowcaseKind = "demand" | "customers" | "emails" | "price" | "feature" | "first_sale";
export interface Showcase {
  kind: ShowcaseKind;
  count: number;
  /** "feature": the listing with the most favorites and no sale. */
  listingId?: number;
}
export interface Showcases {
  /** Only for perks the plan lacks; a section shows its own (when above zero). */
  counts: Partial<Record<ShowcaseKind, number>>;
  /** The one Inicio shows (null: none above zero, or VMAX's before the first sale). */
  pick: Showcase | null;
}
